import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { X, Calendar, MapPin, Users, Plus } from 'lucide-react';
import WeekdayScheduleEditor from './WeekdayScheduleEditor';
import { validateDaySchedules } from '../../../utils/weekdaySchedule';

export default function AddRecurrenceModal({ isOpen, course, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        day_schedules: [],
        start_date: new Date().toISOString().slice(0, 10),
        end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        location: '',
        notes: ''
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        if (error) setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const scheduleError = validateDaySchedules(formData.day_schedules);
        if (scheduleError === 'empty') {
            setError(t('gym.courses.addRecurrenceModal.messages.noDaysSelected'));
            return;
        }
        if (scheduleError === 'time') {
            setError(t('gym.courses.weekdaySchedule.invalidTime'));
            return;
        }

        if (formData.start_date >= formData.end_date) {
            setError(t('gym.courses.addRecurrenceModal.messages.invalidDateRange'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const api = getAuthenticatedApi();
            const response = await api.post('/gym_courses.php?action=add_recurrences', {
                course_id: course.id,
                day_schedules: formData.day_schedules,
                recurrence_days: formData.day_schedules.map((slot) => slot.day),
                start_date: formData.start_date,
                end_date: formData.end_date,
                start_time: formData.day_schedules[0].start_time,
                end_time: formData.day_schedules[0].end_time,
                max_participants: course.max_participants,
                location: formData.location || null,
                notes: formData.notes || null,
            });

            if (response.data.success) {
                const sessionsCount = response.data.sessions_created || 0;
                toast.success(t('gym.courses.addRecurrenceModal.messages.success', { count: sessionsCount }));
                onSuccess();
            } else {
                throw new Error(response.data.error || t('gym.courses.addRecurrenceModal.messages.createError'));
            }
        } catch (err) {
            console.error('Errore aggiunta lezioni:', err);
            setError(err.response?.data?.error || err.message || t('gym.courses.addRecurrenceModal.messages.createError'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t('gym.courses.addRecurrenceModal.title', { courseTitle: course.title })}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
                            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                        </div>
                    )}

                    <div className="mb-6">
                        <WeekdayScheduleEditor
                            slots={formData.day_schedules}
                            defaults={{
                                start_time: course.standard_start_time?.slice(0, 5) || '09:00',
                                end_time: course.standard_end_time?.slice(0, 5) || '10:00',
                            }}
                            onChange={(day_schedules) => {
                                setFormData((prev) => ({ ...prev, day_schedules }));
                                if (error) setError(null);
                            }}
                        />
                    </div>

                    {/* Date */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Calendar className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.startDateLabel')}
                            </label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                required
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Calendar className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.endDateLabel')}
                            </label>
                            <input
                                type="date"
                                name="end_date"
                                value={formData.end_date}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                required
                            />
                        </div>
                    </div>

                    {/* Anteprima */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <MapPin className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.locationLabel')}
                            </label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                placeholder={t('gym.courses.addRecurrenceModal.fields.locationPlaceholder')}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                                <Users className="inline w-4 h-4 mr-1" />
                                {t('gym.courses.addRecurrenceModal.fields.notesLabel')}
                            </label>
                            <input
                                type="text"
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder={t('gym.courses.addRecurrenceModal.fields.notesPlaceholder')}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    {/* Anteprima */}
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                            {t('gym.courses.addRecurrenceModal.preview.title')}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-white/65">
                            {t('gym.courses.addRecurrenceModal.preview.description', { 
                                startDate: formData.start_date, 
                                endDate: formData.end_date 
                            })}
                        </p>
                        {formData.day_schedules.length > 0 && (
                            <p className="mt-1 text-sm text-gray-600 dark:text-white/65">
                                {formData.day_schedules.map((slot) => (
                                    `${t(`gym.courses.addRecurrenceModal.daysOfWeek.${slot.day}`)} ${slot.start_time}–${slot.end_time}`
                                )).join(', ')}
                            </p>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-white/85 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg"
                        >
                            {t('gym.courses.addRecurrenceModal.actions.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading || formData.day_schedules.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <Plus size={16} />
                            )}
                            {t('gym.courses.addRecurrenceModal.actions.create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
